import { Controller } from "@hotwired/stimulus";
import axios from "axios";
import PaymentModalService from "../../service/payment/payment_modal_service";
import PaymentMiddlewareUrlService from "../../service/payment/payment_middleware_url_service";

export default class extends Controller {
  static values = {
    coupon: String,
    newHome: Boolean,
    locale: String,
    lockedCurrency: String,
  };

  static targets = [
    "originalLiteYearPrice",
    "originalPremiumYearPrice",
    "originalLiteMonthPrice",
    "originalPremiumMonthPrice",
    "liteYear",
    "premiumYear",
    "lite",
    "premium",
    "finalLiteYearPrice",
    "finalPremiumYearPrice",
    "saveLiteYear",
    "saveLite",
    "savePremium",
    "free",
    "monthlyPlan",
    "annualPlan",
    "getPremiumYearBtn",
    "getPremiumMonthBtn",
    "getLiteYearBtn",
    "getLiteMonthBtn",
    "currencyContainer",
    "selectedCurrency",
    "currencyContainerForm",
  ];

  symbolMap = {};

  paypalAllowedMap = {};

  upiAllowedMap = {};

  plansData = {};

  connect() {
    this.locale =
      this.localeValue || window.location.pathname.split("/")[1].toUpperCase();

    const premiumSwitch = document.getElementById("premiumSwitch");
    if (premiumSwitch) {
      if (premiumSwitch.checked) {
        this.selectMonthPlan();
      } else {
        this.selectAnnualPlan();
      }
    }
    let planPriceRequestData = {};
    if (this.couponValue) {
      planPriceRequestData = {
        coupon: this.couponValue,
      };
    }

    const getAllCurrencies = axios("/get-all-currencies", { params: { pageFilter: "HOME_PAGE" } })
      .catch((error) => {
        console.error("Failed to load currencies, falling back to USD", error);
        return { data: [{ code: "usd", sign: "$", isAllowedForPayPal: true }] };
      });
    const getAllPlans = axios.post("/get-all-plans", planPriceRequestData)
      .catch((error) => {
        console.error("Failed to load plans", error);
        return { data: {} };
      });

    Promise.all([
      getAllCurrencies,
      getAllPlans,
    ]).then(([{ data: allCurrencies }, { data: plansData }]) => {
      this.plansData = plansData;
      this.symbolMap = {};
      this.paypalAllowedMap = {};
      this.upiAllowedMap = {};

      let currencies = allCurrencies;
      if (this.lockedCurrencyValue) {
        const locked = allCurrencies.filter(
          (currency) => currency.code.toLowerCase() === this.lockedCurrencyValue.toLowerCase()
        );
        if (locked.length > 0) {
          currencies = locked;
        }
      }

      currencies.forEach((currency) => {
        this.symbolMap[currency.code] = currency.sign;
        this.paypalAllowedMap[currency.code] = currency.isAllowedForPayPal;
        this.upiAllowedMap[currency.code] = currency.isAllowedForUpi;
      });

      this.buildCurrencyDropdown(currencies);

      const defaultCurrency = currencies[0]?.code ?? "usd";
      this.setData(defaultCurrency);
    });
  }

  buildCurrencyDropdown(currencies) {
    const currencyContainer = this.currencyContainerTarget;
    const selectedCurrency = this.selectedCurrencyTarget;
    const dropdownList = currencyContainer.querySelector('.currency-drop');
    const hiddenSelect = this.currencyContainerFormTarget;

    dropdownList.innerHTML = '';
    hiddenSelect.innerHTML = '';

    currencies.forEach((currency, index) => {
      const code = currency.code.toLowerCase();

      const listItem = document.createElement('li');
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'dropdown-item cursor-pointer';
      button.setAttribute('data-value', code);
      button.textContent = code.toUpperCase();
      listItem.appendChild(button);
      dropdownList.appendChild(listItem);

      const option = document.createElement('option');
      option.value = code;
      option.textContent = code.toUpperCase();
      if (index === 0) {
        option.selected = true;
      }
      hiddenSelect.appendChild(option);
    });

    if (currencies.length > 0) {
      selectedCurrency.textContent = currencies[0].code.toUpperCase();
    }

    dropdownList.querySelectorAll('.dropdown-item').forEach(item => {
      item.addEventListener('click', (e) => {
        e.preventDefault();
        const value = item.getAttribute('data-value');
        selectedCurrency.textContent = value.toUpperCase();

        hiddenSelect.value = value;

        const changeEvent = new Event('change', { bubbles: true });
        hiddenSelect.dispatchEvent(changeEvent);
      });
    });
  }
  setData(currency) {
    if (this.couponValue !== "") {
      this.setOriginalPrice(
        this.originalLiteYearPriceTargets,
        this.plansData["lite_year"],
        currency,
        true
      );
      this.setOriginalPrice(
        this.originalPremiumYearPriceTargets,
        this.plansData["premium_year"],
        currency,
        true
      );
      this.setOriginalPrice(
        this.originalLiteMonthPriceTargets,
        this.plansData["lite_month"],
        currency
      );
      this.setOriginalPrice(
        this.originalPremiumMonthPriceTargets,
        this.plansData["premium_month"],
        currency
      );

      this.setFinalPrice(
        this.finalLiteYearPriceTargets,
        this.plansData["lite_year"],
        currency
      );
      this.setFinalPrice(
        this.finalPremiumYearPriceTargets,
        this.plansData["premium_year"],
        currency
      );
    }

    this.setPrice(
      this.liteYearTargets,
      this.plansData["lite_year"],
      currency,
      true
    );
    this.setPrice(
      this.premiumYearTargets,
      this.plansData["premium_year"],
      currency,
      true
    );
    this.setPrice(
      this.liteTargets,
      this.plansData["lite_month"],
      currency,
      false
    );
    this.setPrice(
      this.premiumTargets,
      this.plansData["premium_month"],
      currency,
      false
    );
    this.calculateSavePrice(currency);
    this.freeTargets.forEach(
      (target) => (target.innerHTML = `${this.symbolMap[currency]}0`)
    );
  }

  setOriginalPrice(targets, data, currency, isYear) {
    const currencyData = data?.find((item) => item.currency === currency);
    if (!currencyData) {
      return;
    }

    const originalPrice = currencyData['price_per_unit'];
    targets.forEach((target) => {
      const amount = isYear ? (originalPrice / 12).toFixed(2) : originalPrice;
      const priceString = `${this.symbolMap[currency]}${amount}`;

      target.innerHTML = priceString.length > 5 ? '' : priceString;
    });
  }

  setPrice(targets, data, currency, isYear) {
    const currencyData = data?.find((item) => item.currency === currency);
    if (!currencyData) {
      return;
    }

    const price = currencyData.amount;
    targets.forEach((target) => {
      const amount = isYear ? (price / 12).toFixed(2) : price;
      target.innerHTML = `${this.symbolMap[currency]}${amount}`;
    });
  }

  setFinalPrice(targets, data, currency) {
    const currencyData = data?.find((item) => item.currency === currency);
    if (!currencyData) {
      return;
    }

    const price = currencyData['amount'];
    targets.forEach((target) => {
      target.innerHTML = `${this.symbolMap[currency]}${price}`;
    });
  }

  calculateSavePrice(currency) {
    const liteYearData = this.plansData["lite_year"]?.find((item) => item.currency === currency);
    const liteMonthData = this.plansData["lite_month"]?.find((item) => item.currency === currency);
    const premiumYearData = this.plansData["premium_year"]?.find((item) => item.currency === currency);
    const premiumMonthData = this.plansData["premium_month"]?.find((item) => item.currency === currency);

    if (!liteYearData || !liteMonthData || !premiumYearData || !premiumMonthData) {
      return;
    }

    const liteAmountYear = liteYearData['amount'];
    const liteMonthPricePerUnit = liteMonthData['price_per_unit'];
    const liteMonthPriceFor12Month = liteMonthPricePerUnit * 12;
    const liteYearProfit = this.roundPrice(liteMonthPriceFor12Month - liteAmountYear);

    const premiumAmountYear = premiumYearData['amount'];
    const premiumMonthPricePerUnit = premiumMonthData['price_per_unit'];
    const premiumMonthPriceFor12Month = premiumMonthPricePerUnit * 12;
    const premiumYearProfit = this.roundPrice(premiumMonthPriceFor12Month - premiumAmountYear);

    this.saveLiteTargets.forEach((target) => {
      target.innerHTML = `${this.symbolMap[currency]}${liteYearProfit}`;
    });

    this.savePremiumTargets.forEach((target) => {
      target.innerHTML = `${this.symbolMap[currency]}${premiumYearProfit}`;
    });
  }

  roundPrice(price)
  {
    price = Number(price.toFixed(2));
    if (price % 1 === 0) {
      return Math.round(price);
    }

    return price;
  }

  toggle(event) {
    this.setData(event.target.value);
  }

  togglePlan(event) {
    if (event.target.checked) {
      this.selectMonthPlan();
    } else {
      this.selectAnnualPlan();
    }
  }

  selectMonthPlan() {
    this.monthlyPlanTargets.forEach((item) => {
      item.classList.add("d-none");
    });
    this.annualPlanTargets.forEach((item) => {
      item.classList.remove("d-none");
    });
  }

  selectAnnualPlan() {
    this.monthlyPlanTargets.forEach((item) => {
      item.classList.remove("d-none");
    });
    this.annualPlanTargets.forEach((item) => {
      item.classList.add("d-none");
    });
  }

  getPlanAmount(subscriptionType, currency) {
    return this.plansData[subscriptionType]
      ?.find((item) => item.currency === currency.toLowerCase())
      ?.amount;
  }

  getPlanBtn(e) {
    const userId = document.querySelector("[data-user-id]")?.dataset.userId;
    const currency = this.currencyContainerFormTarget.value.toUpperCase();
    let itemName;
    let idx;
    let price;
    const btnRadio1 = document.getElementById("btnradio1");
    const premiumSwitch = document.getElementById("premiumSwitch");
    const type = btnRadio1
      ? (btnRadio1.checked ? "Yearly" : "Monthly")
      : (premiumSwitch && premiumSwitch.checked ? "Yearly" : "Monthly");
    const isAllowedForPayPal = this.paypalAllowedMap[currency.toLowerCase()] ?? false;
    const isAllowedForUpi = this.upiAllowedMap[currency.toLowerCase()] ?? false;

    if (e.params.label === "free") {
      itemName = "Free";
      idx = 1;
      price = 0;
    } else if (e.params.label === "lite") {
      itemName = "Lite";
      idx = 2;
      price = type === "Yearly"
        ? Number(this.liteYearTarget.innerText.replace(/[^\d.]/g, "")) * 12
        : Number(this.liteTarget.innerText.replace(/[^\d.]/g, ""));
      PaymentModalService.setChoosePaymentMethodActions({
        cardBtnActiion: "checkoutLiteMultisubCard",
        paypalBtnAction: "checkoutLiteMultisubPaypal",
        subscriptionType: type === "Yearly" ? PaymentMiddlewareUrlService.liteYearType : PaymentMiddlewareUrlService.liteMonthType,
        coupon: this.couponValue,
        currency: currency.toLowerCase(),
        isAllowedForPayPal,
        isAllowedForUpi,
        // Naver Ads expects KRW regardless of the currency the user picked
        krwAmount: this.getPlanAmount(
          type === "Yearly" ? PaymentMiddlewareUrlService.liteYearType : PaymentMiddlewareUrlService.liteMonthType,
          "krw",
        ),
        planName: `${itemName} ${type}`,
      });
    } else if (e.params.label === "premium") {
      itemName = "Premium";
      idx = 3;
      price = type === "Yearly" ? Number(this.premiumYearTarget.innerText.replace(/[^\d.]/g, "")) * 12 : Number(this.premiumTarget.innerText.replace(/[^\d.]/g, ""));
      PaymentModalService.setChoosePaymentMethodActions({
        cardBtnActiion: "checkoutPremiumMultisubCard",
        paypalBtnAction: "checkoutPremiumMultisubPaypal",
        subscriptionType: type === "Yearly" ? PaymentMiddlewareUrlService.premiumYearType : PaymentMiddlewareUrlService.premiumMonthType,
        coupon: this.couponValue,
        currency: currency.toLowerCase(),
        isAllowedForPayPal,
        isAllowedForUpi,
        // Naver Ads expects KRW regardless of the currency the user picked
        krwAmount: this.getPlanAmount(
          type === "Yearly" ? PaymentMiddlewareUrlService.premiumYearType : PaymentMiddlewareUrlService.premiumMonthType,
          "krw",
        ),
        planName: `${itemName} ${type}`,
      });
    }

    gtag("event", "select_item", {
      site_language: this.locale && this.locale.length > 2 ? "EN" : this.locale || "EN",
      items: [{
        item_name: itemName,
        item_id: `subscription_${e.params.label}_${userId}`,
        price: price.toFixed(2),
        currency: currency,
        item_category: "Pricing",
        item_list_name: "Payment&Subscription",
        item_variant: type,
        index: idx
      }]
    });
    if (typeof rdt === "function") {
      rdt("track", "Custom", { customEventName: "select_item" });
    }
  }

  multiSubscribeNavigate(e) {
    const locale = document.querySelector('[data-locale]').getAttribute('data-locale')
    document.cookie = `multiSub=1; path=${locale === 'en' ? "" : `/${locale}`}/subscription`;
    window.location.href = e.target.dataset.href;
  }
}
